/**
 * Match Element Editor
 * Two-column layout: Items to Match (left) | Options (right)
 */

import * as React from 'react';
import {
    CardTitle,
    Button,
    Badge,
    Label,
    Textarea,
    cn,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@ai-content-hub/content-kit';
import type { ElementEditorProps } from '@ai-content-hub/content-kit';
import { Plus, Trash2 } from 'lucide-react';
import type { BlockContent, BlockContentType } from '../block';

import {
    type MatchElementData,
    type MatchItem,
    type MatchOption,
    normalizeMatchData,
    createDefaultItem,
    createDefaultOption,
    matchElementSchema,
} from './schema';
import { BlockEditor, createDefaultBlockContent } from '../block';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';

const MATCH_ALLOWED_TYPES: BlockContentType[] = ['text', 'image', 'audio'];

export interface MatchEditProps extends ElementEditorProps<MatchElementData> {}

export const MatchEdit: React.FC<MatchEditProps> = ({
    value,
    onChange,
    disabled = false,
    className,
    config,
    schema: schemaProp,
    language,
}) => {
    const schema = schemaProp ?? matchElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });

    const data = React.useMemo(() => normalizeMatchData(value || {}), [value]);

    const updateData = (updates: Partial<MatchElementData>) => {
        onChange({ ...data, ...updates });
    };

    const addItem = () => {
        if (disabled || data.items.length >= 8) return;
        const rank = data.items.length + 1;
        const newItem = createDefaultItem(rank);
        updateData({ items: [...data.items, newItem] });
    };

    const removeItem = (itemId: string) => {
        if (disabled || data.items.length <= 2) return;
        updateData({ items: data.items.filter((i) => i.id !== itemId) });
    };

    const addOption = () => {
        if (disabled || data.options.length >= 12) return;
        const newOption = createDefaultOption(data.options.length);
        updateData({ options: [...data.options, newOption] });
    };

    const removeOption = (optionId: string) => {
        if (disabled || data.options.length <= 2) return;
        const removedOptId = optionId;
        updateData({
            options: data.options.filter((o) => o.id !== removedOptId),
            items: data.items.map((i) =>
                i.correctOptionId === removedOptId ? { ...i, correctOptionId: '' } : i
            ),
        });
    };

    const updateItem = (itemId: string, updates: Partial<MatchItem>) => {
        if (disabled) return;
        updateData({
            items: data.items.map((i) => (i.id === itemId ? { ...i, ...updates } : i)),
        });
    };

    const updateOption = (optionId: string, updates: Partial<MatchOption>) => {
        if (disabled) return;
        updateData({
            options: data.options.map((o) => (o.id === optionId ? { ...o, ...updates } : o)),
        });
    };

    const handleItemContentChange = (itemId: string) => (content: BlockContent) => {
        updateItem(itemId, { content });
    };

    const matchDefaultAlignment = 'center' as const;

    const handleItemTypeChange = (itemId: string) => (type: BlockContentType) => {
        const item = data.items.find((i) => i.id === itemId);
        if (!item || item.content.type === type) return;
        updateItem(itemId, { content: createDefaultBlockContent(type, { alignment: matchDefaultAlignment }) });
    };

    const handleOptionContentChange = (optionId: string) => (content: BlockContent) => {
        updateOption(optionId, { content });
    };

    const handleOptionTypeChange = (optionId: string) => (type: BlockContentType) => {
        const option = data.options.find((o) => o.id === optionId);
        if (!option || option.content.type === type) return;
        updateOption(optionId, { content: createDefaultBlockContent(type, { alignment: matchDefaultAlignment }) });
    };

    return (
        <div className={cn('match-edit', 'flex flex-col gap-8', className)}>
            <div className={cn('match-edit-columns', 'grid grid-cols-1 lg:grid-cols-2 gap-8')}>
                {/* Left Column - Items to Match */}
                <div className={cn('match-edit-items-section', 'flex flex-col gap-4')}>
                <div className={cn('match-edit-items-header', 'flex items-center justify-between gap-4')}>
                    <CardTitle className="text-lg font-semibold">{t('itemsToMatch')}</CardTitle>
                    <Badge variant="secondary" className={cn('match-edit-items-badge', 'bg-info/10 text-info shrink-0')}>
                        {data.items.length}
                    </Badge>
                </div>
                <div className={cn('match-edit-items-list', 'space-y-4')}>
                    {data.items.map((item) => (
                        <div key={item.id} className={cn('match-edit-item', 'rounded-sm border bg-muted/30 p-2')}>
                            <div className="flex items-start gap-3">
                                <Badge
                                    variant="secondary"
                                    className={cn('match-edit-item-badge', 'bg-info/10 text-info shrink-0 w-7 justify-center')}
                                >
                                    #{item.rank}
                                </Badge>
                                <div className="flex-1 min-w-0">
                                    <BlockEditor
                                        layout="oneline"
                                        content={item.content}
                                        onChange={handleItemContentChange(item.id)}
                                        onTypeChange={handleItemTypeChange(item.id)}
                                        disabled={disabled}
                                        placeholder={t('enterItem')}
                                        allowedTypes={MATCH_ALLOWED_TYPES}
                                        defaultAlignment={matchDefaultAlignment}
                                    />
                                </div>
                                <Select
                                    value={item.correctOptionId || ''}
                                    onValueChange={(v) => updateItem(item.id, { correctOptionId: v })}
                                    disabled={disabled}
                                >
                                    <SelectTrigger className="w-24 h-9">
                                        <SelectValue placeholder="?" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {data.options.map((opt) => (
                                            <SelectItem key={opt.id} value={opt.id}>
                                                {opt.letter}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className={cn('match-edit-item-remove-button', 'h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/5 shrink-0')}
                                    onClick={() => removeItem(item.id)}
                                    disabled={disabled || data.items.length <= 2}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className={cn('match-edit-add-item-button', 'w-full border-dashed border-border')}
                        onClick={addItem}
                        disabled={disabled || data.items.length >= 8}
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        {t('addQuestionItem')}
                    </Button>
                </div>
            </div>

            {/* Right Column - Options */}
            <div className={cn('match-edit-options-section', 'flex flex-col gap-4 lg:border-l lg:border-border lg:pl-8')}>
                <div className={cn('match-edit-options-header', 'flex items-center justify-between gap-4')}>
                    <CardTitle className="text-lg font-semibold">{t('matchingOptionsHeading')}</CardTitle>
                    <Badge variant="secondary" className={cn('match-edit-options-badge', 'bg-success/10 text-success shrink-0')}>
                        {data.options.length}
                    </Badge>
                </div>
                <div className={cn('match-edit-options-list', 'space-y-4')}>
                    {data.options.map((option) => (
                        <div key={option.id} className={cn('match-edit-option', 'rounded-sm border bg-muted/30 p-2')}>
                            <div className="flex items-start gap-3">
                                <Badge
                                    variant="secondary"
                                    className={cn('match-edit-option-badge', 'bg-success/10 text-success shrink-0 w-9 justify-center')}
                                >
                                    {option.letter}
                                </Badge>
                                <div className="flex-1 min-w-0">
                                    <BlockEditor
                                        layout="oneline"
                                        content={option.content}
                                        onChange={handleOptionContentChange(option.id)}
                                        onTypeChange={handleOptionTypeChange(option.id)}
                                        disabled={disabled}
                                        placeholder={t('enterOption')}
                                        allowedTypes={MATCH_ALLOWED_TYPES}
                                        defaultAlignment={matchDefaultAlignment}
                                    />
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className={cn('match-edit-option-remove-button', 'h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/5 shrink-0')}
                                    onClick={() => removeOption(option.id)}
                                    disabled={disabled || data.options.length <= 2}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className={cn('match-edit-add-option-button', 'w-full border-dashed border-border')}
                        onClick={addOption}
                        disabled={disabled || data.options.length >= 12}
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        {t('addMatchingOption')}
                    </Button>
                </div>
            </div>
            </div>

            {/* Explanation */}
            <div className={cn('match-edit-explanation', 'space-y-2')}>
                <Label htmlFor="match-explanation">{t('explanationAfterSubmit')}</Label>
                <Textarea
                    id="match-explanation"
                    value={data.explanation ?? ''}
                    onChange={(e) => updateData({ explanation: e.target.value })}
                    placeholder={t('explainCorrectAnswer')}
                    disabled={disabled}
                    rows={3}
                    className="match-edit-explanation-input"
                />
            </div>
        </div>
    );
};

export default MatchEdit;
